import { usePersistedState } from "@/lib/persistedFilters";
import { useEffect, useMemo, useState } from "react";
import { ArrowDown, ArrowUp, CalendarClock, CheckCircle2, FileDown, History, Loader2, Lock, Pencil, Printer, Search, Settings2 } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { MODEL_LABEL, fmtPrice, groupByCategory } from "@/components/pricelist/priceUtils";
import { PriceHistoryDialog } from "@/components/pricelist/PriceHistoryDialog";
import { PriceListAdmin } from "@/components/pricelist/PriceListAdmin";
import { PriceListSettings } from "@/components/pricelist/PriceListSettings";

const FULL = { msrp: "MSRP", reseller: "Reseller", sp: "Service Provider" };

function PriceCell({ c, change }) {
  return (
    <td className="px-3 py-2 text-right align-top tabular-nums whitespace-nowrap">
      {c.na ? <span className="text-slate-400">N/A</span>
        : c.on_request ? <span className="text-slate-400">Price on request</span>
        : <span className="text-slate-800">{fmtPrice(c.price)}</span>}
      {change && <ChangeChip r={change} />}
    </td>
  );
}

const ChangeChip = ({ r }) => (
  <div className={`mt-0.5 text-[10px] ${r.direction === "up" ? "text-red-600" : r.direction === "down" ? "text-emerald-600" : "text-amber-600"}`}
       title={`Takes effect ${r.effective_date}`}>
    {r.direction === "up" ? <ArrowUp className="inline h-3 w-3" /> : r.direction === "down" ? <ArrowDown className="inline h-3 w-3" /> : null}
    {r.removed ? "Removed" : r.new_na ? "→ N/A" : `→ ${r.new === null ? "Price on request" : fmtPrice(r.new)}`} · {r.effective_date}
  </div>
);

function AckBar({ b, canAck, onAck }) {
  if (b.acknowledged) return <span className="ml-auto flex items-center gap-1 text-xs font-normal text-emerald-700" data-testid={`price-ack-done-${b.batch_id}`}><CheckCircle2 className="h-3.5 w-3.5" /> Read by {b.acknowledged.user_name} on {b.acknowledged.at.slice(0, 10)}</span>;
  if (!canAck) return null;
  return <Button size="sm" variant="outline" className="ml-auto h-7 text-xs" onClick={() => onAck(b.batch_id)} data-testid={`price-ack-${b.batch_id}`}><CheckCircle2 className="h-3.5 w-3.5" /> I have read this change</Button>;
}

function UpcomingTable({ upcoming, canAck, onAck }) {
  if (!upcoming.length) return <p className="p-6 text-sm text-slate-500" data-testid="price-upcoming-empty">No price changes are scheduled.</p>;
  return upcoming.map((b) => (
    <div key={b.batch_id} className="border-b border-slate-100 p-4" data-testid={`price-upcoming-${b.batch_id}`}>
      <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-700">
        <CalendarClock className="h-4 w-4 text-amber-500" /> Takes effect {b.effective_date}
        <AckBar b={b} canAck={canAck} onAck={onAck} />
      </div>
      {b.message && <p className="mb-2 whitespace-pre-line rounded-md border-l-4 border-amber-400 bg-amber-50 px-3 py-2 text-xs text-amber-900"
                       data-testid={`price-upcoming-message-${b.batch_id}`}>{b.message}</p>}
      <table className="w-full text-sm">
        <thead><tr className="text-left text-[10px] uppercase tracking-wider text-slate-400">
          <th className="py-1">Item or Service</th><th>Price model</th><th className="text-right">Current</th><th className="text-right">New</th><th className="text-right">Change</th>
        </tr></thead>
        <tbody>{b.rows.map((r, i) => (
          <tr key={i} className="border-t border-slate-100">
            <td className="py-1.5">{r.name}<div className="text-[10px] text-slate-400">{r.unit}</div></td>
            <td className="text-slate-500">{FULL[r.model]}</td>
            <td className="text-right tabular-nums">{r.old === null ? <span className="text-slate-400">Price on request</span> : fmtPrice(r.old)}</td>
            <td className="text-right tabular-nums font-medium">{r.removed ? "Removed" : r.new_na ? "N/A" : r.new === null ? "Price on request" : fmtPrice(r.new)}
              {r.new_note && <div className="text-[10px] font-normal text-slate-400">{r.new_note}</div>}</td>
            <td className={`text-right tabular-nums ${r.direction === "up" ? "text-red-600" : "text-emerald-600"}`}>
              {r.direction && r.direction !== "same" ? `${r.direction === "up" ? "+" : "−"}${fmtPrice(Math.abs(r.new - r.old))}` : "—"}</td>
          </tr>
        ))}</tbody>
      </table>
    </div>
  ));
}

export default function PriceList() {
  const { user, has } = useAuth();
  const canManage = ["create", "edit", "export"].some((a) => has(`price_list.${a}`));
  const canSettings = user?.role === "super_admin" || has("price_list_approve.view");
  const isStaff = (user?.base_role || user?.role) === "staff" || user?.role === "super_admin";
  const [as, setAs] = useState("all");
  const [data, setData] = useState(null);
  const [params] = useSearchParams();
  const allowed = ["list", "upcoming", canManage && "manage", canSettings && "settings"].filter(Boolean);
  const want = params.get("tab") || (params.get("edit") === "1" ? "manage" : "list");
  const [tab, setTab] = useState(allowed.includes(want) ? want : "list");
  const [pdfBusy, setPdfBusy] = useState(false);
  const [histItem, setHistItem] = useState(null);
  const [asOf, setAsOf] = useState(params.get("as_of") || "");
  const today = new Date().toLocaleDateString("en-CA");
  const [q, setQ] = usePersistedState("price-list.q", "");
  const [cat, setCat] = useState("");
  const [blocked, setBlocked] = useState("");

  const load = () => api.get("/price-list", { params: { as_: isStaff ? as : "", as_of: asOf } }).then(({ data: d }) => setData(d))
    .catch((e) => {
      const msg = formatApiErrorDetail(e.response?.data?.detail) || "Could not load the price list";
      if (e.response?.status === 403) setBlocked(msg); else toast.error(msg);
    });
  useEffect(() => { load(); }, [as, isStaff, asOf]); // eslint-disable-line react-hooks/exhaustive-deps
  const ack = async (id) => {
    try { await api.post(`/price-list/batches/${id}/ack`); toast.success("Thanks — marked as read"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save"); }
  };
  const openPdf = async (print) => {
    setPdfBusy(true);
    try {
      const { data: blob } = await api.get("/price-list/pdf", { params: { as_: isStaff ? as : "", as_of: asOf }, responseType: "blob" });
      const url = URL.createObjectURL(blob);
      if (print) { const w = window.open(url, "_blank"); w?.addEventListener("load", () => w.print()); }
      else { const a = document.createElement("a"); a.href = url; a.download = "price-list.pdf"; a.click(); }
    } catch { toast.error("Could not build the PDF"); } finally { setPdfBusy(false); }
  };

  const changes = useMemo(() => {
    const m = {};
    (data?.upcoming || []).forEach((b) => b.rows.forEach((r) => { m[`${r.item_id}:${r.model}`] = { ...r, effective_date: b.effective_date }; }));
    return m;
  }, [data]);
  const groups = useMemo(() => {
    const t = q.trim().toLowerCase();
    const items = (data?.items || []).filter((i) => (!cat || i.category === cat)
      && (!t || `${i.name} ${i.unit} ${i.description}`.toLowerCase().includes(t)));
    return groupByCategory(items);
  }, [data, q, cat]);

  if (blocked && !data) return (
    <div className="mx-auto mt-24 max-w-md text-center" data-testid="price-list-unavailable">
      <Lock className="mx-auto h-8 w-8 text-slate-300" />
      <p className="mt-3 text-sm text-slate-600">{blocked}</p>
    </div>
  );
  if (!data) return <Loader2 className="mx-auto mt-24 h-6 w-6 animate-spin text-slate-300" />;
  const cats = [...new Set(data.items.map((i) => i.category))];
  const upCount = data.upcoming.reduce((s, b) => s + b.rows.length, 0);
  const adminTab = tab === "manage" || tab === "settings";
  const go = (k) => { if (adminTab) load(); setTab(k); };
  const tabs = [["list", "Price list"], ["upcoming", `Upcoming changes (${upCount})`],
    canManage && ["manage", "Manage prices", Pencil], canSettings && ["settings", "Settings", Settings2]].filter(Boolean);

  return (
    <div className="mx-auto max-w-[1400px] px-4 py-6" data-testid="price-list-page">
      <PageHeader testid="price-list-header" title="Price List"
                  subtitle={`All prices in CAD · showing ${data.models.map((m) => FULL[m]).join(" and ")} pricing`}>
        {!adminTab && <>
        <Button variant="outline" size="sm" disabled={pdfBusy} onClick={() => openPdf(false)} data-testid="price-pdf-download">
          {pdfBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />} Download PDF</Button>
        <Button variant="outline" size="sm" disabled={pdfBusy} onClick={() => openPdf(true)} data-testid="price-pdf-print">
          <Printer className="h-4 w-4" /> Print</Button>
        {isStaff && (
          <select value={as} onChange={(e) => setAs(e.target.value)} data-testid="price-list-view-as"
                  className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm">
            <option value="all">Staff view — all models</option><option value="agent">View as Agent</option>
            <option value="reseller">View as Reseller</option><option value="sp">View as Service Provider</option>
          </select>
        )}
        </>}
      </PageHeader>
      {!adminTab && upCount > 0 && (
        <button type="button" onClick={() => setTab("upcoming")} data-testid="price-upcoming-banner"
                className="mb-4 flex w-full items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 text-left text-sm text-amber-800 hover:bg-amber-100">
          <CalendarClock className="h-4 w-4" /> {upCount} upcoming price change{upCount === 1 ? "" : "s"} — next on {data.upcoming[0].effective_date}. View details
        </button>
      )}
      <div className="mb-3 flex flex-wrap items-center gap-2">
        {tabs.map(([k, l, Icon]) => (
          <button key={k} type="button" onClick={() => go(k)} data-testid={`price-tab-${k}`}
                  className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${tab === k ? "bg-slate-900 text-white" : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"}`}>
            {Icon && <Icon className="h-3.5 w-3.5" />}{l}</button>
        ))}
        {tab === "list" && <>
          <label className="ml-auto flex items-center gap-1.5 text-xs text-slate-500">As of
            <Input type="date" value={asOf} max={today} onChange={(e) => setAsOf(e.target.value && e.target.value < today ? e.target.value : "")}
                   className="h-9 w-40" data-testid="price-as-of" /></label>
          <div className="relative w-64"><Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search items…" className="pl-8" data-testid="price-search" /></div>
          <select value={cat} onChange={(e) => setCat(e.target.value)} data-testid="price-category-filter"
                  className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm">
            <option value="">All categories</option>{cats.map((c) => <option key={c}>{c}</option>)}
          </select>
        </>}
      </div>
      {tab === "manage" && <PriceListAdmin />}
      {tab === "settings" && <PriceListSettings />}
      {!adminTab && <>
      {data.as_of && tab === "list" && (
        <div className="mb-3 flex items-center gap-2 rounded-lg border border-sky-200 bg-sky-50 px-4 py-2 text-sm text-sky-800" data-testid="price-as-of-banner">
          <History className="h-4 w-4" /> Showing the price list as it stood on <b>{data.as_of}</b>.
          <button type="button" className="ml-auto text-xs font-semibold underline" onClick={() => setAsOf("")} data-testid="price-as-of-reset">Back to today</button>
        </div>
      )}
      <Card className="overflow-hidden border-slate-200 shadow-sm">
        {tab === "upcoming" ? <UpcomingTable upcoming={data.upcoming} canAck={data.can_ack} onAck={ack} /> : (
          <div className="max-h-[70vh] overflow-auto">
            <table className="w-full text-sm" data-testid="price-list-table">
              <thead className="sticky top-0 z-10 bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                <tr><th className="px-3 py-2 text-left">Item or Service</th>
                  {data.models.map((m) => [<th key={m} className="px-3 py-2 text-right">{MODEL_LABEL[m]} Price</th>,
                    <th key={`${m}n`} className="px-3 py-2 text-left">{MODEL_LABEL[m]} Note</th>])}</tr>
              </thead>
              <tbody>{groups.map((g) => [
                <tr key={g.category} className="bg-slate-100/70"><td colSpan={1 + data.models.length * 2}
                    className="px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-slate-600">{g.category}</td></tr>,
                ...g.items.map((i) => (
                  <tr key={i.item_id} className="border-t border-slate-100 hover:bg-slate-50/60" data-testid={`price-row-${i.item_id}`}>
                    <td className={`px-3 py-2 align-top ${i.parent_id ? "pl-8 text-slate-600" : "font-medium text-slate-800"}`}>
                      {i.parent_id && <span className="mr-1 text-slate-300">↳</span>}{i.name}
                      {i.has_history && (
                        <button type="button" onClick={() => setHistItem(i)} title="Price history" data-testid={`price-history-${i.item_id}`}
                                className="ml-1.5 inline-flex items-center gap-0.5 rounded px-1 text-[10px] font-normal text-slate-400 hover:bg-slate-100 hover:text-slate-700">
                          <History className="h-3 w-3" /> History</button>
                      )}
                      {i.unit && <div className="text-[11px] font-normal text-slate-400">{i.unit}</div>}
                      {i.description && <div className="max-w-xl text-[11px] font-normal text-slate-400">{i.description}</div>}
                    </td>
                    {data.models.map((m) => [
                      <PriceCell key={m} c={i.prices[m]} change={changes[`${i.item_id}:${m}`]} />,
                      <td key={`${m}n`} className="px-3 py-2 align-top text-[11px] text-slate-400">{i.prices[m].note}</td>])}
                  </tr>
                ))])}
              </tbody>
            </table>
            {groups.length === 0 && <p className="p-6 text-sm text-slate-500" data-testid="price-list-empty">
              {data.as_of && !data.items.length ? `Nothing was published on the price list yet on ${data.as_of}.` : "No items match."}</p>}
          </div>
        )}
      </Card>
      </>}
      {histItem && <PriceHistoryDialog item={histItem} as={isStaff ? as : ""} onClose={() => setHistItem(null)} />}
    </div>
  );
}
